/**
 * useSetPassword Hook
 *
 * Sets initial password using URQL mutation.
 */

import { useCallback } from "react";
import { useMutation } from "urql";
import { errorTracking } from "@/lib/error-tracking";

// =============================================================================
// MUTATION DOCUMENT
// =============================================================================

const SetPasswordDocument = `
  mutation SetPassword($password: String!) {
    setPassword(password: $password)
  }
`;

// =============================================================================
// TYPES
// =============================================================================

interface UseSetPasswordResult {
  setPassword: (password: string) => Promise<boolean>;
  isLoading: boolean;
  error: Error | null;
}

// =============================================================================
// HOOK
// =============================================================================

/**
 * Set initial password for account.
 */
export function useSetPassword(): UseSetPasswordResult {
  const [result, executeMutation] = useMutation<
    { setPassword: boolean },
    { password: string }
  >(SetPasswordDocument);

  const setPassword = useCallback(
    async (password: string): Promise<boolean> => {
      try {
        const response = await executeMutation({ password });

        if (response.error) {
          throw new Error(
            response.error.graphQLErrors[0]?.message ||
              response.error.message ||
              "Failed to set password"
          );
        }

        errorTracking.addBreadcrumb({
          category: "profile",
          message: "Password set",
        });

        return response.data?.setPassword ?? true;
      } catch (err) {
        const passwordError =
          err instanceof Error ? err : new Error("Failed to set password");

        errorTracking.captureError(passwordError, {
          tags: { feature: "profile", action: "setPassword" },
        });

        throw passwordError;
      }
    },
    [executeMutation]
  );

  return {
    setPassword,
    isLoading: result.fetching,
    error: result.error ?? null,
  };
}
